import { FormControl, FormLabel, Grid, Typography } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import AddApplicantButton from "./AddApplicantButton";
import SelectInput from "../../../../components/SelectInput";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";
import { applicantCountConstant } from "../../../../config/constant";

const ApplicantMappingRow = React.memo(
  ({
    formik,
    unitNo = "",
    applicantsPath = "",
    applicantKeys = ["applicant1", "applicant2", "applicant3", "applicant4"],
    applicants = {},
    applicantOptions = [],
    isDisabled = false,
    canAdd = true,
    canRemove = true,
    readOnly = false,
    isIndividualFlow = true,
  }) => {
    const { t } = useTranslation();

    const unitApplicants = useMemo(() => {
      if (readOnly) return applicants || {};
      return getIn(formik.values, applicantsPath) || {};
    }, [applicants, applicantsPath, formik.values, readOnly]);

    const visibleApplicants = useMemo(
      () => applicantKeys.filter((key) => unitApplicants[key] !== null),
      [unitApplicants, applicantKeys]
    );

    const handleAddApplicant = useCallback(() => {
      const emptyField = applicantKeys.find(
        (key) => unitApplicants[key] === null
      );
      if (emptyField) {
        formik.setFieldValue(`${applicantsPath}.${emptyField}`, "");
      }
    }, [unitApplicants, applicantsPath, formik, applicantKeys]);

    const handleRemoveApplicant = useCallback(
      (field) => {
        const applicants = { ...getIn(formik.values, applicantsPath) };
        const indexToRemove = applicantKeys.indexOf(field);

        if (indexToRemove === -1) return;

        for (let i = indexToRemove; i < applicantKeys.length - 1; i++) {
          applicants[applicantKeys[i]] = applicants[applicantKeys[i + 1]];
        }

        applicants[applicantKeys.at(-1)] = null;

        formik.setFieldValue(applicantsPath, applicants);
      },
      [applicantsPath, formik, applicantKeys]
    );

    const maxApplicants = applicantKeys.length;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <Typography
            sx={{
              color: "#1c252e",
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "22px",
            }}
          >
            {unitNo}
            <sup />
          </Typography>
        </Grid>

        <Grid item xs={12} sm={12} md={9} lg={9}>
          <Grid container spacing={3}>
            {visibleApplicants.map((field, index) => (
              <Grid key={field} item xs={12} sm={6} md={4} lg={4}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{
                      color: "#1c252e",
                      fontSize: "14px",
                      fontWeight: "500",
                      lineHeight: "22px",
                      mb: "6px",
                    }}
                  >
                    {index === 0 ? (
                      <span>
                        {isIndividualFlow
                          ? t("applicantMapping.labels.primaryApplicant")
                          : t("applicantMapping.labels.authorizedSignatory")}
                        <sup />
                      </span>
                    ) : (
                      <span>
                        {index + 1}
                        <sup>
                          {applicantCountConstant?.numberKeys[index + 1]}
                        </sup>{" "}
                        {isIndividualFlow
                          ? t("applicantMapping.labels.applicant")
                          : t("applicantMapping.labels.authorizedSignatory")}
                        <sup />
                      </span>
                    )}
                    {index !== 0 && canRemove && (
                      <RemoveCircleIcon
                        sx={{
                          color: "#ff0000",
                          height: "20px",
                          width: "20px",
                          verticalAlign: "middle",
                          marginLeft: 2,
                          cursor: "pointer",
                        }}
                        onClick={() => handleRemoveApplicant(field)}
                      />
                    )}
                  </FormLabel>
                  {readOnly ? (
                    <Typography
                      sx={{
                        border: "1px solid #00000061",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        fontSize: "14px",
                        background: "#ffffff00",
                        color: "#00000061",
                      }}
                    >
                      {unitApplicants?.[field]}
                    </Typography>
                  ) : (
                    <SelectInput
                      field={{
                        name: `${applicantsPath}.${field}`,
                        required: false,
                        readonly: false,
                        isDisableField: isDisabled,
                        options:
                          field === applicantKeys[0]
                            ? applicantOptions.filter(
                                (option) => !(option?.isMinor)
                              )
                            : applicantOptions,
                        placeholder: "Select",
                      }}
                      formik={formik}
                    />
                  )}
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {visibleApplicants.length < maxApplicants && canAdd && (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            display="flex"
            justifyContent="center"
          >
            <AddApplicantButton
              title={t("applicantMapping.buttons.addApplicant", {
                entity: isIndividualFlow ? "Applicant" : "Authorized Signatory",
              })}
              onClick={handleAddApplicant}
            />
          </Grid>
        )}
      </Grid>
    );
  }
);

export default ApplicantMappingRow;
